import { Application, ApplicationStatus } from '@mkp/application/models';
import {
  applicationApiActions,
  applicationPageActions,
  applicationStatusApiActions,
} from '@mkp/application/state/actions';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';

export interface State extends EntityState<Application> {
  applicationStatuses: ApplicationStatus[];
}

const adapter: EntityAdapter<Application> = createEntityAdapter<Application>();

const initialState: State = adapter.getInitialState({
  applicationStatuses: [],
});

export const updateOneActions = [applicationApiActions.applicationRefreshSuccess];

export const updateManyActions = [
  applicationApiActions.applicationsUpdateEnded,
  applicationApiActions.applicationsReloadForStatusAlreadyChangedSuccess,
];

const reducer = createReducer(
  initialState,
  on(
    applicationStatusApiActions.applicationStatusesLoadedSuccess,
    (state, { applicationStatuses }) => ({
      ...state,
      // dedup shouldn't be necessary, it's more of a safety measure, move statuses to @ngrx/entity to handle this better
      applicationStatuses: mergeAndDedupArrays(state.applicationStatuses, applicationStatuses),
    })
  ),
  on(
    applicationPageActions.opened,
    applicationPageActions.stepChanged,
    applicationPageActions.reloadNewTabAfterApplicationAdded,
    (state) =>
      adapter.removeAll({
        ...state,
      })
  ),
  on(applicationPageActions.searchedApplicationSelected, (state, { applicationId, offset }) =>
    state.ids[offset] === applicationId ? state : adapter.removeAll({ ...state })
  ),
  on(
    applicationApiActions.applicationsLoadedSuccess,
    applicationApiActions.applicationsLoadedForSearchedApplicationSuccess,
    (state, { applications }) =>
      adapter.setAll(applications, {
        ...state,
      })
  ),

  on(
    applicationApiActions.moreApplicationsLoadedSuccess,
    applicationApiActions.moreApplicationsLoadedAfterChangeSuccess,
    applicationApiActions.moreApplicationsLoadedForSearchedApplicationSuccess,
    (state, { applications }) =>
      adapter.upsertMany(applications, {
        ...state,
      })
  ),
  on(applicationApiActions.routeApplicationLoadedSuccess, (state, { application }) =>
    adapter.upsertOne(application, { ...state })
  ),
  on(applicationApiActions.applicationsDeleteEnded, (state, { successes }) =>
    adapter.removeMany(
      successes.map(({ id }) => id),
      state
    )
  ),
  on(
    applicationApiActions.applicationsUpdateEnded,
    applicationApiActions.applicationsDeleteEnded,
    (state, { errors404 }) =>
      adapter.removeMany(
        errors404.map(({ applicationId }) => applicationId),
        { ...state }
      )
  ),
  on(applicationApiActions.applicationRefreshNotFound, (state, { applicationId }) =>
    adapter.removeOne(applicationId, state)
  ),
  on(...updateOneActions, (state, { application }) =>
    adapter.updateOne({ id: application.id, changes: application }, { ...state })
  ),
  on(
    applicationApiActions.applicationsReadRefreshSuccess,
    (state, { applications, missingApplicationIds }) =>
      adapter.updateMany(
        applications.map((application) => ({ id: application.id, changes: application })),
        adapter.removeMany(missingApplicationIds, state)
      )
  ),
  on(...updateManyActions, (state, action) =>
    adapter.updateMany(
      getApplicationsFromUpdateManyAction(action).map((application) => ({
        id: application.id,
        changes: application,
      })),
      {
        ...state,
      }
    )
  )
);
const feature = createFeature({
  name: 'application',
  reducer,
});

const { selectAll } = adapter.getSelectors(feature.selectApplicationState);

export const applicationFeature = { ...feature, selectAll };

const mergeAndDedupArrays = (
  statuses1: ApplicationStatus[],
  statuses2: ApplicationStatus[]
): ApplicationStatus[] =>
  Object.values(
    [...statuses1, ...statuses2].reduce((acc, status) => ({ ...acc, [status.id]: status }), {})
  );

export { State as ApplicationState };

const getApplicationsFromUpdateManyAction = (
  action: ReturnType<(typeof updateManyActions)[number]>
): Application[] =>
  action.type === applicationApiActions.applicationsReloadForStatusAlreadyChangedSuccess.type
    ? action.reloadedApplications
    : action.successes;
